import { apiClient } from '@/api/client';
import { uploadDirect } from '@/api/direct-upload.api';

/* ── Vocabulary ───────────────────────────────────────────── */

/**
 * What a person can file.
 *
 * `crash` is absent on purpose: crashes are recorded by the app, not filed by a
 * user, and they never appear in a clinic's list. See `reportCrash` below.
 */
export type TicketType = 'bug' | 'incorrect_data' | 'feature';

export type TicketStatus =
  | 'new'
  | 'acknowledged'
  | 'planned'
  | 'in_progress'
  | 'resolved'
  | 'wont_do';

/** Statuses that still count as waiting on us. */
export const OPEN_STATUSES: TicketStatus[] = [
  'new',
  'acknowledged',
  'planned',
  'in_progress',
];

export const isClosed = (status: TicketStatus) =>
  status === 'resolved' || status === 'wont_do';

/* ── Shapes ───────────────────────────────────────────────── */

/**
 * The page state captured with a report.
 *
 * Everything here is collected by the app, and shown to the user before they
 * submit — collecting page state silently would be worse than not collecting
 * it at all.
 */
export interface CapturedContext {
  pageUrl?: string;
  pageName?: string;
  appVersion?: string;
  dateRangeFrom?: string;
  dateRangeTo?: string;
  filters?: Record<string, unknown>;
  userAgent?: string;
  viewport?: string;
  screen?: string;
  role?: string;
  language?: string;
}

export interface IncorrectDataDetails {
  figureLabel?: string;
  shownValue?: string;
  expectedValue?: string;
}

/**
 * A ticket as the clinic sees it.
 *
 * Note what is *not* here: no admin notes, no debug payload, no severity, no
 * assignee. The API does not send them; this type says so, so a component
 * cannot reach for one and quietly get `undefined`.
 */
export interface Ticket {
  id: number;
  type: TicketType;
  status: TicketStatus;
  title: string;
  description: string;
  expectedBehaviour: string | null;
  stepsToReproduce: string | null;
  submissionDetails: IncorrectDataDetails | null;
  friendlySummary: string | null;
  resolutionNote: string | null;
  pageUrl: string | null;
  capturedContext: CapturedContext | null;
  branch: { id: string; name: string } | null;
  reporter: { id: string; name: string } | null;
  unreadReplies: number;
  lastAdminReplyAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TicketComment {
  id: number;
  body: string;
  /** `clinic` or `admin` — drives which side of the thread it sits on. */
  authorKind: 'clinic' | 'admin' | 'system';
  author: { id: string; name: string } | null;
  createdAt: string;
  attachmentIds: number[];
}

export interface TicketEvent {
  id: number;
  kind: string;
  toValue: string | null;
  note: string | null;
  actorKind: string;
  createdAt: string;
}

export interface TicketAttachment {
  id: number;
  mime: string;
  size: number;
  label: string | null;
  commentId: number | null;
  /** Short-lived and signed. Refresh via `attachmentUrl` if the page is stale. */
  url: string | null;
  createdAt: string;
}

export interface TicketDetail {
  ticket: Ticket;
  comments: TicketComment[];
  events: TicketEvent[];
  attachments: TicketAttachment[];
}

export interface TicketListResponse {
  items: Ticket[];
  total: number;
  page: number;
  limit: number;
  counts: Record<string, number>;
}

export interface CreateTicketPayload {
  type: TicketType;
  title: string;
  description: string;
  expectedBehaviour?: string;
  stepsToReproduce?: string;
  incorrectData?: IncorrectDataDetails;
  branchId?: string;
  context?: CapturedContext;
}

export interface ListQuery {
  type?: TicketType;
  /** `open` (default) | `closed` | `all`, or a specific status. */
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

/* ── Client ───────────────────────────────────────────────── */

export const feedbackApi = {
  async create(payload: CreateTicketPayload): Promise<Ticket> {
    const { data } = await apiClient.post<Ticket>('/feature-requests', payload);
    return data;
  },

  async list(query: ListQuery = {}): Promise<TicketListResponse> {
    const { data } = await apiClient.get<TicketListResponse>(
      '/feature-requests',
      { params: query },
    );
    return data;
  },

  async detail(id: number): Promise<TicketDetail> {
    const { data } = await apiClient.get<TicketDetail>(
      `/feature-requests/${id}`,
    );
    return data;
  },

  async reply(id: number, body: string): Promise<TicketComment> {
    const { data } = await apiClient.post<TicketComment>(
      `/feature-requests/${id}/comments`,
      { body },
    );
    return data;
  },

  async reopen(id: number, reason?: string): Promise<void> {
    await apiClient.post(`/feature-requests/${id}/reopen`, { reason });
  },

  async markRead(id: number): Promise<void> {
    await apiClient.post(`/feature-requests/${id}/read`, {});
  },

  /**
   * Upload one attachment against a ticket. The file goes straight to
   * storage first; the API gets its key.
   */
  async attach(
    id: number,
    file: File,
    opts: { label?: string; commentId?: number } = {},
  ): Promise<TicketAttachment> {
    const uploadKey = await uploadDirect(file, 'ticket-attachment');
    const { data } = await apiClient.post<TicketAttachment>(
      `/feature-requests/${id}/attachments`,
      {
        uploadKey,
        label: opts.label || undefined,
        commentId: opts.commentId || undefined,
      },
    );
    return data;
  },

  async attachmentUrl(id: number, attachmentId: number): Promise<string> {
    const { data } = await apiClient.get<{ url: string }>(
      `/feature-requests/${id}/attachments/${attachmentId}/url`,
    );
    return data.url;
  },
};

/* ── Crash reporting ──────────────────────────────────────── */

export interface CrashReportPayload {
  errorName: string;
  errorMessage: string;
  stack?: string;
  componentStack?: string;
  sentryEventId?: string;
  context?: CapturedContext;
}

/**
 * Record a crash.
 *
 * Deliberately not part of `feedbackApi`: this does not create a ticket and
 * nothing it records appears in the clinic's list. It groups server-side by
 * fingerprint, so a recurring bug is one issue for the team rather than one row
 * per person who hit it.
 *
 * Returns whether it got through rather than throwing. The caller is an error
 * screen: it wants to tell the user "sent" or "that didn't work, try again",
 * and neither an exception to catch nor a silent success is useful there.
 */
export async function reportCrash(
  payload: CrashReportPayload,
): Promise<boolean> {
  try {
    await apiClient.post('/crashes', payload);
    return true;
  } catch {
    return false;
  }
}
